import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
    Box,
    Button,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventIcon from "@mui/icons-material/Event";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import { fontSize } from "theme/tokens";
import { getLabelByKey, useChangeableItems } from "store/useDynamicLabel";

const AddInterviewScheduler = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { addInterviewSchedule, interviewId } = location.state || {};
    const instituteId = localStorage.getItem("InstituteId");
    const isEditMode = addInterviewSchedule !== "true";
    const [loading, setLoading] = useState(false);
    const [overAllLoading, setOverAllLoading] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [interviewers, setInterviewers] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [drives, setDrives] = useState([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [loadingInterviewers, setLoadingInterviewers] = useState(false);

    const changeableItems = useChangeableItems();
    const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

    // Validation Schema
    const schema = Yup.object().shape({
        drive: Yup.number()
            .required("Please select a drive")
            .typeError("Please select a drive"),
        candidate: Yup.number()
            .required("Please select a candidate")
            .typeError("Please select a candidate"),
        interviewer: Yup.number()
            .required("Please select an interviewer")
            .typeError("Please select an interviewer"),
        jobRole: Yup.number()
            .required("Please select a job role")
            .typeError("Please select a job role"),
        scheduledDate: Yup.string()
            .required("Please select date"),

        scheduledAt: Yup.string()
            .required("Please select scheduled time")
            .test("not-past-time", "Please select a future time", function (value) {
                if (!value || !this.parent.scheduledDate) return true;

                const scheduledDateTime = dayjs(
                    `${this.parent.scheduledDate} ${value}`,
                    "YYYY-MM-DD HH:mm"
                );

                return scheduledDateTime.isAfter(dayjs());
            }),
        duration_minutes: Yup.number()
            .required("Please enter duration")
            .typeError("Please enter duration")
            .min(1, "Duration must be at least 1 minute"),
        meeting_url: Yup.string().nullable(),
        job_description: Yup.string().nullable(),

        // Edit-only fields
        status: isEditMode
            ? Yup.string().required("Please select status")
            : Yup.string().nullable(),
        interview_feedback: Yup.string().nullable(),
        rating: Yup.number()
            .transform((value, originalValue) =>
                originalValue === "" ? null : value
            )
            .nullable()
            .typeError("Please select rating")
            .min(1, "Rating must be between 1 and 5")
            .max(5, "Rating must be between 1 and 5"),
        result: isEditMode
            ? Yup.string().required("Please select result")
            : Yup.string().nullable(),
    });

    // React Hook Form
    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            drive: "",
            candidate: "",
            interviewer: "",
            jobRole: "",
            scheduledDate: "",
            scheduledAt: "",
            duration_minutes: "",
            meeting_url: "",
            status: "",
            interview_feedback: "",
            rating: "",
            result: "",
            job_description: ""
        },
    });

    const selectedDrive = watch("drive");

    // Common styles
    const textFieldStyles = {
        "& .MuiOutlinedInput-root": {
            fontSize: '14px',
            "& fieldset": {
                borderColor: "white",
            },
            "&:hover fieldset": {
                borderColor: "#d1d1d1",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#1c97e3",
            },
        },
        "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "white",
        },
        "&:hover .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "#d1d1d1",
        },
        "& .Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "#1c97e3",
        },
        "& .Mui-disabled": {
            cursor: "not-allowed",
        },
    };

    // Get Drives
    const getDrivesList = async () => {
        try {
            const response = await api.post("getAllBatchList");
            const driveData = Array.isArray(response?.data)
                ? response.data
                : response?.data?.data || [];
            setDrives(driveData);
            return driveData;
        } catch (error) {
            console.error("Error fetching drives:", error);
            setDrives([]);
            showSnackbar("error", "Failed to load drives");
            return [];
        }
    };

    // Get Candidates based on selected Drive
    const getCandidatesList = async (batchId) => {
        if (!batchId) {
            setCandidates([]);
            return [];
        }
        setLoadingCandidates(true);
        try {
            const response = await api.post("InterviewCandidatesDropdown", {
                batch_id: batchId,
            });
            const candidateData = Array.isArray(response?.data)
                ? response.data
                : response?.data?.data || [];
            setCandidates(candidateData);
            return candidateData;
        } catch (error) {
            console.error("Error fetching candidates:", error);
            setCandidates([]);
            showSnackbar("error", "Failed to load candidates");
            return [];
        } finally {
            setLoadingCandidates(false);
        }
    };

    // Get Interviewers
    const getInterviewersList = async () => {
        setLoadingInterviewers(true);
        try {
            const response = await api.post("InterviewersDropdown", {});
            if (response?.data?.status === "success") {
                setInterviewers(response?.data?.data || []);
            } else {
                setInterviewers([]);
            }
        } catch (error) {
            console.error("Error fetching interviewers:", error);
            setInterviewers([]);
            showSnackbar("error", "Failed to load interviewers");
        } finally {
            setLoadingInterviewers(false);
        }
    };

    // Get Programs / Job Roles
    const getProgramsList = async () => {
        try {
            const response = await api.post("GetProgramsDropdown", {
                institute_id: +instituteId,
            });
            setPrograms(response?.data?.data || []);
        } catch (error) {
            console.error("Error fetching programs:", error);
            setPrograms([]);
        }
    };

    // Get Existing Interview Details for Edit
    const getInterviewDetails = async () => {
        if (!interviewId) return;
        try {
            const response = await api.post("ViewInterviewByID", {
                interview_id: interviewId,
            });
            const interview = response?.data?.data || {};
            console.log("Interview details:", interview);

            // Set Drive first
            setValue("drive", interview?.batch_id || "");

            // Load candidates for this drive
            await getCandidatesList(interview?.batch_id);

            // Set all existing values
            setValue("candidate", interview?.candidate?.user_id || "");
            setValue("interviewer", interview?.interviewer?.user_id || "");
            setValue("jobRole", interview?.program_id || "");

            if (interview?.scheduled_at) {
                const scheduledDateTime = dayjs(interview.scheduled_at);
                setValue("scheduledDate", scheduledDateTime.format("YYYY-MM-DD"));
                setValue("scheduledAt", scheduledDateTime.format("HH:mm"));
            }

            setValue("duration_minutes", interview?.duration_minutes || "");
            setValue("meeting_url", interview?.meeting_url || "");
            setValue("status", interview?.status || "");
            setValue("interview_feedback", interview?.interview_feedback || "");
            setValue("rating", interview?.rating ?? "");
            setValue("result", interview?.result || "");
            setValue("job_description", interview?.program_outcome || "")
        } catch (error) {
            console.error("Error fetching interview details:", error);
            showSnackbar(
                "error",
                error?.response?.data?.message || "Failed to load interview details"
            );
        }
    };

    // Initial API calls
    useEffect(() => {
        const initialize = async () => {
            setOverAllLoading(true);
            try {
                await Promise.all([
                    getDrivesList(),
                    getInterviewersList(),
                    getProgramsList(),
                ]);
                if (isEditMode && interviewId) {
                    await getInterviewDetails();
                }
            } catch (error) {
                console.error("Initialization error:", error);
                showSnackbar("error", "An error occurred while loading form data");
            } finally {
                setOverAllLoading(false);
            }
        };
        initialize();
    }, []);

    // Drive changed -> Get Candidates
    useEffect(() => {
        if (!selectedDrive) {
            setCandidates([]);
            if (!isEditMode) {
                setValue("candidate", "");
            }
            return;
        }
        if (isEditMode) return;
        setValue("candidate", "");
        getCandidatesList(selectedDrive);
    }, [selectedDrive]);

    // Submit
    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const scheduledDateTime = data?.scheduledDate && data?.scheduledAt
                ? dayjs(
                    `${data.scheduledDate} ${data.scheduledAt}`,
                    "YYYY-MM-DD HH:mm"
                ).format("YYYY-MM-DDTHH:mm:ssZ")
                : null;

            const payload = {
                candidate_user_id: Number(data?.candidate),
                interviewer_user_id: Number(data?.interviewer),
                program_id: Number(data?.jobRole),
                batch_id: Number(data?.drive),
                scheduled_at: scheduledDateTime,
                duration_minutes: Number(data?.duration_minutes),
                meeting_url: data?.meeting_url?.trim() || null,
            };

            if (isEditMode) {
                payload.interview_id = Number(interviewId);
                payload.status = data?.status;
                payload.interview_feedback = data?.interview_feedback?.trim() || null;
                payload.rating = data?.rating === "" || data?.rating == null
                    ? null
                    : Number(data?.rating);
                payload.result = data?.result;
            }

            const endpoint = isEditMode ? "UpdateInterview" : "ScheduleInterview";
            console.log("Submitting:", endpoint, payload);

            const response = await api.post(endpoint, payload);

            if (response?.data?.status === "success") {
                showSnackbar(
                    "success",
                    response?.data?.message ||
                    (isEditMode
                        ? "Interview updated successfully"
                        : "Interview scheduled successfully")
                );
                navigate("/interview-scheduler-list");
            } else {
                showSnackbar(
                    "error",
                    response?.data?.message ||
                    (isEditMode
                        ? "Failed to update interview"
                        : "Failed to schedule interview")
                );
            }
        } catch (error) {
            console.error("Error submitting scheduler:", error);
            showSnackbar(
                "error",
                error?.response?.data?.message ||
                error?.response?.data?.detail ||
                "An error occurred during submission"
            );
        } finally {
            setLoading(false);
        }
    };

    const jobRole = watch("jobRole")

    const getJobDescription = async () => {
        try {
            const response = await api.post("GetProgramOutcome", { program_id: jobRole })
            setValue("job_description", response?.data?.data?.program_outcome || "")
        } catch (err) {
            console.error("Failed to get the jd")
        }
    }

    useEffect(() => {
        getJobDescription()
    }, [jobRole])

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ minHeight: "100vh", p: 1 }}>
                {loading && <OverAllLoader message={isEditMode ? "Updating Schedule..." : "Saving Schedule..."} />}

                {overAllLoading ? (
                    <OverAllLoader message="Loading Scheduler..." />
                ) : (
                    <Box>
                        {/* Back Button */}
                        <Grid
                            item
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "#1c97e3",
                                gap: "4px",
                                cursor: "pointer",
                                width: "40px",
                                mb: 2,
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    window.history.back();
                                }
                            }}
                            onClick={() => window.history.back()}
                        >
                            <ArrowBackIcon sx={{ fontSize: "17px" }} />
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: "#1c97e3",
                                }}
                            >
                                Back
                            </Typography>
                        </Grid>

                        {/* Header */}
                        <Box>
                            <Grid
                                sx={{
                                    pb: 2,
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                    borderBottom: "1px solid #d1d1d1",
                                }}
                            >
                                <Grid
                                    sx={{
                                        backgroundColor: "#1c97e3",
                                        p: 0.5,
                                        borderRadius: "10px",
                                    }}
                                >
                                    <EventIcon
                                        sx={{
                                            color: "white",
                                            fontSize: "35px",
                                        }}
                                    />
                                </Grid>
                                <Grid>
                                    <Typography
                                        sx={{
                                            fontSize: "30px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {addInterviewSchedule === "true"
                                            ? "Add Scheduler"
                                            : "Update Scheduler"}
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Form */}
                            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: 2,
                                        p: 3,
                                    }}
                                >
                                    {/* Scheduler Information */}
                                    <Grid
                                        sx={{
                                            display: "flex",
                                            gap: 1,
                                            alignItems: "center",
                                            mb: 1,
                                        }}
                                    >
                                        <EventIcon
                                            sx={{
                                                color: "#1c97e3",
                                                fontSize: "30px",
                                                fontWeight: "bold",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: "20px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Scheduler Information
                                        </Typography>
                                    </Grid>

                                    {/* ROW 1: DRIVE + CANDIDATE */}
                                    <Grid sx={{ display: "flex", gap: 3 }}>
                                        {/* Drive */}
                                        <Grid sx={{ width: "100%" }}>
                                            <Typography>
                                                Drive{" "}
                                                <span style={{ color: "red" }}>
                                                    *
                                                </span>
                                            </Typography>
                                            <Controller
                                                name="drive"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        value={field.value || ""}
                                                        select
                                                        fullWidth
                                                        error={!!errors.drive}
                                                        helperText={errors.drive?.message}
                                                        slotProps={{
                                                            select: {
                                                                displayEmpty: true,
                                                                renderValue: (selected) => {
                                                                    if (!selected) {
                                                                        return (
                                                                            <span style={{ color: "#999" }}>
                                                                                Select Drive
                                                                            </span>
                                                                        );
                                                                    }
                                                                    const drive = drives.find(
                                                                        (item) =>
                                                                            String(item.batch_id) ===
                                                                            String(selected)
                                                                    );
                                                                    return drive?.batch_name || "";
                                                                },
                                                            },
                                                        }}
                                                        sx={textFieldStyles}
                                                    >
                                                        <MenuItem value="" disabled>
                                                            Select Drive
                                                        </MenuItem>
                                                        {drives?.map((item) => (
                                                            <MenuItem
                                                                key={item.batch_id}
                                                                value={item.batch_id}
                                                            >
                                                                {item.batch_name}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            />
                                        </Grid>

                                        {/* Candidate */}
                                        <Grid sx={{ width: "100%" }}>
                                            <Typography>
                                                {`${getLabel('STUDENT')}`}{" "}
                                                <span style={{ color: "red" }}>
                                                    *
                                                </span>
                                            </Typography>
                                            <Controller
                                                name="candidate"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        value={field.value || ""}
                                                        select
                                                        fullWidth
                                                        disabled={!selectedDrive || loadingCandidates}
                                                        error={!!errors.candidate}
                                                        helperText={errors.candidate?.message}
                                                        slotProps={{
                                                            select: {
                                                                displayEmpty: true,
                                                                renderValue: (selected) => {
                                                                    if (loadingCandidates) {
                                                                        return (
                                                                            <span style={{ color: "#999" }}>
                                                                                Loading Candidates...
                                                                            </span>
                                                                        );
                                                                    }
                                                                    if (!selected) {
                                                                        return (
                                                                            <span style={{ color: "#999" }}>
                                                                                {selectedDrive
                                                                                    ? `Select ${getLabel('STUDENT')}`
                                                                                    : "Select Drive First"}
                                                                            </span>
                                                                        );
                                                                    }
                                                                    const candidate = candidates.find(
                                                                        (item) =>
                                                                            String(item.user_id) ===
                                                                            String(selected)
                                                                    );
                                                                    return candidate?.name || "";
                                                                },
                                                            },
                                                        }}
                                                        sx={textFieldStyles}
                                                    >
                                                        <MenuItem value="" disabled>
                                                            {selectedDrive
                                                                ? `Select ${getLabel('STUDENT')}`
                                                                : "Select Drive First"}
                                                        </MenuItem>
                                                        {candidates?.map((item) => (
                                                            <MenuItem
                                                                key={item.user_id}
                                                                value={item.user_id}
                                                            >
                                                                {item.name}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* ROW 2: INTERVIEWER + PROGRAM */}
                                    <Grid sx={{ display: "flex", gap: 3 }}>
                                        {/* Interviewer */}
                                        <Grid sx={{ width: "100%" }}>
                                            <Typography>
                                                {`${getLabel('INTERVIEWER')}`}{" "}
                                                <span style={{ color: "red" }}>
                                                    *
                                                </span>
                                            </Typography>
                                            <Controller
                                                name="interviewer"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        value={field.value || ""}
                                                        select
                                                        fullWidth
                                                        disabled={loadingInterviewers}
                                                        error={!!errors.interviewer}
                                                        helperText={errors.interviewer?.message}
                                                        slotProps={{
                                                            select: {
                                                                displayEmpty: true,
                                                                renderValue: (selected) => {
                                                                    if (loadingInterviewers) {
                                                                        return (
                                                                            <span style={{ color: "#999" }}>
                                                                                Loading Interviewers...
                                                                            </span>
                                                                        );
                                                                    }
                                                                    if (!selected) {
                                                                        return (
                                                                            <span style={{ color: "#999" }}>
                                                                                {` Select ${getLabel('INTERVIEWER')}`}
                                                                            </span>
                                                                        );
                                                                    }
                                                                    const interviewer = interviewers.find(
                                                                        (item) =>
                                                                            String(item.user_id) ===
                                                                            String(selected)
                                                                    );
                                                                    return interviewer?.name || "";
                                                                },
                                                            },
                                                        }}
                                                        sx={textFieldStyles}
                                                    >
                                                        <MenuItem value="" disabled>
                                                            {`Select ${getLabel('INTERVIEWER')}`}
                                                        </MenuItem>
                                                        {interviewers?.map((item) => (
                                                            <MenuItem
                                                                key={item.user_id}
                                                                value={item.user_id}
                                                            >
                                                                {item.name}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            />
                                        </Grid>

                                        {/* Program / Job Role */}
                                        <Grid sx={{ width: "100%" }}>
                                            <Typography>
                                                {`${getLabel('PROGRAMS')}`}
                                                <span style={{ color: "red" }}>
                                                    *
                                                </span>
                                            </Typography>
                                            <Controller
                                                name="jobRole"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        value={field.value || ""}
                                                        select
                                                        fullWidth
                                                        error={!!errors.jobRole}
                                                        helperText={errors.jobRole?.message}
                                                        slotProps={{
                                                            select: {
                                                                displayEmpty: true,
                                                                renderValue: (selected) => {
                                                                    if (!selected) {
                                                                        return (
                                                                            <span style={{ color: "#999" }}>
                                                                                {`Select ${getLabel('PROGRAMS')}`}
                                                                            </span>
                                                                        );
                                                                    }
                                                                    const program = programs.find(
                                                                        (item) =>
                                                                            String(item.program_id) ===
                                                                            String(selected)
                                                                    );
                                                                    return program?.program_name || "";
                                                                },
                                                            },
                                                        }}
                                                        sx={textFieldStyles}
                                                    >
                                                        <MenuItem value="" disabled>
                                                            {`Select ${getLabel('PROGRAMS')}`}
                                                        </MenuItem>
                                                        {programs?.map((item) => (
                                                            <MenuItem
                                                                sx={{ fontSize: '14px' }}
                                                                key={item.program_id}
                                                                value={item.program_id}
                                                            >
                                                                {item.program_name}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* ROW 3: JOB DESCRIPTION */}
                                    <Grid sx={{ width: "100%" }}>
                                        <Typography>
                                            Job Description
                                        </Typography>
                                        <Controller
                                            name="job_description"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    type="text"
                                                    placeholder="Enter job description"
                                                    inputProps={{ min: 1 }}
                                                    sx={textFieldStyles}
                                                    multiline
                                                    minRows={4}
                                                    maxRows={8}
                                                    disabled
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* ROW 4: DATE + TIME */}
                                    <Grid sx={{ display: "flex", gap: 3 }}>
                                        {/* Scheduled Date */}
                                        <Grid sx={{ width: "100%" }}>
                                            <Typography>
                                                Scheduled Date{" "}
                                                <span style={{ color: "red" }}>
                                                    *
                                                </span>
                                            </Typography>
                                            <Controller
                                                name="scheduledDate"
                                                control={control}
                                                render={({ field }) => (
                                                    <DatePicker
                                                        value={
                                                            field.value
                                                                ? dayjs(field.value)
                                                                : null
                                                        }
                                                        onChange={(value) => {
                                                            field.onChange(
                                                                value
                                                                    ? value.format("YYYY-MM-DD")
                                                                    : ""
                                                            );
                                                        }}
                                                        minDate={dayjs()}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                error: !!errors.scheduledDate,
                                                                helperText: errors.scheduledDate?.message,
                                                                placeholder: "DD-MM-YYYY",
                                                                sx: textFieldStyles,
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        {/* Scheduled Time */}
                                        <Grid sx={{ width: "100%" }}>
                                            <Typography>
                                                Scheduled Time{" "}
                                                <span style={{ color: "red" }}>
                                                    *
                                                </span>
                                            </Typography>
                                            <Controller
                                                name="scheduledAt"
                                                control={control}
                                                render={({ field }) => (
                                                    <TimePicker
                                                        value={
                                                            field.value
                                                                ? dayjs(`2000-01-01 ${field.value}`)
                                                                : null
                                                        }
                                                        onChange={(value) => {
                                                            field.onChange(
                                                                value
                                                                    ? value.format("HH:mm")
                                                                    : ""
                                                            );
                                                        }}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                error: !!errors.scheduledAt,
                                                                helperText: errors.scheduledAt?.message,
                                                                sx: textFieldStyles,
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* ROW 5: DURATION + MEETING URL */}
                                    <Grid sx={{ display: "flex", gap: 3 }}>
                                        {/* Duration */}
                                        <Grid sx={{ width: "100%" }}>
                                            <Typography>
                                                Duration (Minutes){" "}
                                                <span style={{ color: "red" }}>
                                                    *
                                                </span>
                                            </Typography>
                                            <Controller
                                                name="duration_minutes"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        type="number"
                                                        placeholder="Enter duration"
                                                        error={!!errors.duration_minutes}
                                                        helperText={errors.duration_minutes?.message}
                                                        inputProps={{ min: 1 }}
                                                        sx={textFieldStyles}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        {/* Meeting URL */}
                                        <Grid sx={{ width: "100%" }}>
                                            <Typography>Meeting URL</Typography>
                                            <Controller
                                                name="meeting_url"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        placeholder="Enter meeting URL"
                                                        sx={textFieldStyles}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* ROW 6: STATUS + RESULT */}
                                    {isEditMode && (
                                        <>
                                            <Grid sx={{ display: "flex", gap: 3 }}>
                                                {/* Status */}
                                                <Grid sx={{ width: "100%" }}>
                                                    <Typography>
                                                        Status{" "}
                                                        <span style={{ color: "red" }}>
                                                            *
                                                        </span>
                                                    </Typography>
                                                    <Controller
                                                        name="status"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                value={field.value || ""}
                                                                select
                                                                fullWidth
                                                                error={!!errors.status}
                                                                helperText={errors.status?.message}
                                                                slotProps={{
                                                                    select: {
                                                                        displayEmpty: true,
                                                                        renderValue: (selected) => {
                                                                            if (!selected) {
                                                                                return (
                                                                                    <span style={{ color: "#999" }}>
                                                                                        Select Status
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            return selected;
                                                                        },
                                                                    },
                                                                }}
                                                                sx={textFieldStyles}
                                                            >
                                                                <MenuItem value="" disabled>
                                                                    Select Status
                                                                </MenuItem>
                                                                <MenuItem value="SCHEDULED" sx={{ fontSize: '13px' }}>
                                                                    SCHEDULED
                                                                </MenuItem>
                                                                <MenuItem value="RESCHEDULED" sx={{ fontSize: '13px' }}>
                                                                    RESCHEDULED
                                                                </MenuItem>
                                                                <MenuItem value="CANCELLED" sx={{ fontSize: '13px' }}>
                                                                    CANCELLED
                                                                </MenuItem>
                                                                <MenuItem value="COMPLETED" sx={{ fontSize: '13px' }}>
                                                                    COMPLETED
                                                                </MenuItem>
                                                            </TextField>
                                                        )}
                                                    />
                                                </Grid>

                                                {/* Result */}
                                                <Grid sx={{ width: "100%" }}>
                                                    <Typography>
                                                        Result{" "}
                                                        <span style={{ color: "red" }}>
                                                            *
                                                        </span>
                                                    </Typography>
                                                    <Controller
                                                        name="result"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                value={field.value || ""}
                                                                select
                                                                fullWidth
                                                                error={!!errors.result}
                                                                helperText={errors.result?.message}
                                                                slotProps={{
                                                                    select: {
                                                                        displayEmpty: true,
                                                                        renderValue: (selected) => {
                                                                            if (!selected) {
                                                                                return (
                                                                                    <span style={{ color: "#999" }}>
                                                                                        Select Result
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            return selected;
                                                                        },
                                                                    },
                                                                }}
                                                                sx={textFieldStyles}
                                                            >
                                                                <MenuItem value="" disabled>
                                                                    Select Result
                                                                </MenuItem>
                                                                <MenuItem value="PENDING" sx={{ fontSize: '13px' }}>
                                                                    PENDING
                                                                </MenuItem>
                                                                <MenuItem value="SELECTED" sx={{ fontSize: '13px' }}>
                                                                    SELECTED
                                                                </MenuItem>
                                                                <MenuItem value="REJECTED" sx={{ fontSize: '13px' }}>
                                                                    REJECTED
                                                                </MenuItem>
                                                            </TextField>
                                                        )}
                                                    />
                                                </Grid>
                                            </Grid>

                                            <Grid sx={{ display: "flex", gap: 3 }}>
                                                {/* Interview Feedback */}
                                                <Grid sx={{ width: "100%" }}>
                                                    <Typography>Interview Feedback</Typography>
                                                    <Controller
                                                        name="interview_feedback"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                fullWidth
                                                                multiline
                                                                minRows={3}
                                                                placeholder="Enter interview feedback"
                                                                sx={textFieldStyles}
                                                            />
                                                        )}
                                                    />
                                                </Grid>

                                                {/* Rating */}
                                                <Grid sx={{ width: "100%" }}>
                                                    <Typography>Rating</Typography>
                                                    <Controller
                                                        name="rating"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                value={field.value ?? ""}
                                                                select
                                                                fullWidth
                                                                error={!!errors.rating}
                                                                helperText={errors.rating?.message}
                                                                slotProps={{
                                                                    select: {
                                                                        displayEmpty: true,
                                                                        renderValue: (selected) => {
                                                                            if (selected === "") {
                                                                                return (
                                                                                    <span style={{ color: "#999" }}>
                                                                                        Select Rating
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            return `${selected} / 10`;
                                                                        },
                                                                    },
                                                                }}
                                                                sx={textFieldStyles}
                                                            >
                                                                <MenuItem value="">
                                                                    Select Rating
                                                                </MenuItem>
                                                                <MenuItem value={1}>1 / 10</MenuItem>
                                                                <MenuItem value={2}>2 / 10</MenuItem>
                                                                <MenuItem value={3}>3 / 10</MenuItem>
                                                                <MenuItem value={4}>4 / 10</MenuItem>
                                                                <MenuItem value={5}>5 / 10</MenuItem>
                                                                <MenuItem value={6}>6 / 10</MenuItem>
                                                                <MenuItem value={7}>7 / 10</MenuItem>
                                                                <MenuItem value={8}>8 / 10</MenuItem>
                                                                <MenuItem value={9}>9 / 10</MenuItem>
                                                                <MenuItem value={10}>10 / 10</MenuItem>
                                                            </TextField>
                                                        )}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}

                                    {/* Buttons */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: 2,
                                            mt: 3,
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => window.history.back()}
                                            sx={{
                                                textTransform: "none",
                                                borderRadius: "12px",
                                                px: 3,
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={loading}
                                            sx={{
                                                textTransform: "none",
                                                borderRadius: "12px",
                                                px: 4,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {addInterviewSchedule === "true"
                                                ? "Add Scheduler"
                                                : "Update Scheduler"}
                                        </Button>
                                    </Box>
                                </Box>
                            </form>
                        </Box>
                    </Box>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default AddInterviewScheduler;